/**
 * アクセントの「段階」をプロフィール配下へ流す。
 *
 * 色そのものは ThemeProvider（palette.primary.main）で配っているので prop が要らない。
 * 段階だけは色ではないのでテーマに乗らず、そのままだと
 * ProfilePage → ProfileTab → StatsWidget のような深さまで prop を運ぶことになる。
 * **段階は今後も要素が増える一方**（constants/profileAccent.ts の設計上、
 * 新しい要素は「どの段階に属するか」を決めるだけで済むはずの軸）なので、
 * 運ぶ側が増えないよう色と同じく配下へ配る形にしてある。
 *
 * 要素側は `useAccentReaches('b')` だけを見ること。rank を直接比べない
 * （段階の包含関係を壊さないため。理由は constants/profileAccent.ts）。
 *
 * Provider を置くのは ProfilePage の ThemeProvider と同じ位置。**対で動かすこと。**
 * 片方だけ外へ出すと、色は付くのに段階が既定に戻る（またはその逆）になる。
 */

import { createContext, useContext } from 'react';
import {
  AccentLevelKey,
  DEFAULT_ACCENT_LEVEL,
  accentReaches,
} from '../../constants/profileAccent';

export const ProfileAccentLevelContext = createContext<AccentLevelKey>(DEFAULT_ACCENT_LEVEL);

/** その段階に達しているか。要素側はこれだけを見る */
export const useAccentReaches = (needed: AccentLevelKey): boolean =>
  accentReaches(useContext(ProfileAccentLevelContext), needed);

/* ---- 統計の数値 ----------------------------------------------------------
 * アクセントを乗せてよい対象は「アイコンと罫線、数値まで」。
 * 数値は単語ではなく記号なので、色が付いても読解の負荷にならない
 * （本文に色を乗せない理由と対になっている。constants/profileAccent.ts 参照）。
 *
 * **最上段('f')でだけ色を乗せる。** 数値は面積が小さいわりに最も視線を引くので、
 * 中段('b')の紹介文の面と同時に乗せると一段飛ばしたように見える。
 *
 * ゲーム / アニメ / 映画の3タブに同じ形の数値があるので、
 * **3箇所に同じ分岐を書かず必ずここを通すこと。**
 */

const STATS_NUMBER_BASE = {
  fontSize: { xs: '1.6rem', sm: '2rem' },
  fontWeight: 600,
  letterSpacing: '-0.5px',
  lineHeight: 1.2,
} as const;

const STATS_NUMBER_PLAIN_SX = { ...STATS_NUMBER_BASE, color: '#e6edf3' } as const;
const STATS_NUMBER_ACCENT_SX = { ...STATS_NUMBER_BASE, color: 'primary.main' } as const;

/** ウィジェット上部の大きな数値。最上段に達していなければ今までどおりの白 */
export const useStatsNumberSx = () =>
  useAccentReaches('f') ? STATS_NUMBER_ACCENT_SX : STATS_NUMBER_PLAIN_SX;

/**
 * 上の型に収まらない数値（見出し直下の h3 など）に足す色。
 *
 * **最上段でないときは undefined を返す。** 既定色を白で上書きしないため
 * （この位置の数値は text.primary で描かれていて #e6edf3 とは別の色）。
 */
export const useStatsAccentColor = (): string | undefined =>
  useAccentReaches('f') ? 'primary.main' : undefined;

/* ---- カードの自己スコアのアイコン ----
 * カード左上のスコアバッジの星（既定は金色）。段階とは別の独立した ON/OFF
 * （理由は constants/profileAccent.ts の CardScoreAccentKey）。
 * 隣の数値には乗せない（絵の上の白文字で、地の明度が作品ごとに変わるため）。
 * ゲーム / アニメ / 映画の3カードで共通。3箇所に同じ分岐を書かない。
 */
export const ProfileCardScoreAccentContext = createContext(false);

export const useCardScoreIconColor = (): string =>
  useContext(ProfileCardScoreAccentContext) ? 'primary.main' : 'gold';
